// frontend/src/pages/Home.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import RoleSelectionModal from '../components/common/RoleSelectionModal';
import {
  RocketLaunchIcon,
  ArrowRightOnRectangleIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/solid';
import {
  GlobeAltIcon,
  ShieldCheckIcon,
  BoltIcon,
  ChartBarIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [modalMode, setModalMode]         = useState('register');

  const handleRoleSelect = (role) => {
    setShowRoleModal(false);
    if (modalMode === 'login') {
      navigate(role === 'partner' ? '/login/partner' : '/login/client');
    } else {
      navigate(role === 'partner' ? '/register/partner' : '/register/client');
    }
  };

  const handleRegisterClick = () => { setModalMode('register'); setShowRoleModal(true); };
  const handleLoginClick    = () => { setModalMode('login');    setShowRoleModal(true); };


  const features = [
    {
      icon: <GlobeAltIcon />,
      color: '#2563eb',
      bg: 'rgba(37,99,235,0.07)',
      title: 'Zones non couvertes',
      description: 'Tunisie, Algérie, Maroc… Acceptez les paiements internationaux sans compte Stripe.',
      visual: 'globe',
    },
    {
      icon: <ShieldCheckIcon />,
      color: '#4f46e5',
      bg: 'rgba(79,70,229,0.07)',
      title: 'Paiements sécurisés',
      description: 'Transactions Stripe sécurisées, converties automatiquement en points.',
      visual: 'shield',
    },
    {
      icon: <BoltIcon />,
      color: '#0891b2',
      bg: 'rgba(8,145,178,0.07)',
      title: 'API performante',
      description: 'Idempotence garantie, pas de double débit, logs d\'audit complets.',
      visual: 'api',
    },
    {
      icon: <ChartBarIcon />,
      color: '#10b981',
      bg: 'rgba(16,185,129,0.07)',
      title: 'Tableau de bord',
      description: 'Suivez vos ventes, vos points et vos clients en temps réel.',
      visual: 'chart',
    },
  ];

  const steps = [
    { number: '01', title: 'Partenaire s\'inscrit',     description: 'Créez un compte partenaire, soumettez vos informations pour validation' },
    { number: '02', title: 'Client paie via Stripe',    description: 'Votre client achète des points — Stripe encaisse en EUR depuis n\'importe où' },
    { number: '03', title: 'Points crédités & service', description: 'Les points sont disponibles instantanément et consommés à chaque service utilisé' },
    { number: '04', title: 'Facture & virement',        description: 'Chaque mois, soumettez votre facture — Runit-FR vous vire votre commission sous 30 j' },
  ];

  const problems = [
    { icon: <XCircleIcon />,        title: 'Stripe bloqué en Tunisie & MENA',     desc: 'Impossible d\'accepter des paiements internationaux directement sans entité européenne.' },
    { icon: <CurrencyDollarIcon />, title: 'Conversion manuelle fastidieuse',      desc: 'Gérer vous-même les devises, les taux et les risques de change vous coûte du temps et de l\'argent.' },
    { icon: <ClockIcon />,          title: 'Intégrations de paiement complexes',   desc: 'Chaque API de paiement est différente. Multiplier les intégrations ralentit le time-to-market.' },
  ];

  const testimonials = [
    { name: 'Sarah K.',   role: 'CEO, AI-Vision Edit', avatar: 'SK', rating: 5, content: 'POINTPAY nous a permis d\'accepter des paiements internationaux. Nos revenus ont augmenté de 40 % !' },
    { name: 'Mohamed A.', role: 'Fondateur, PDF Pro',   avatar: 'MA', rating: 5, content: 'L\'intégration est simple, les paiements sont sécurisés. Le support est excellent. Je recommande !' },
    { name: 'Amel B.',    role: 'CTO, Image Enhancer',  avatar: 'AB', rating: 5, content: 'Une solution innovante qui résout un vrai problème. Les points sont crédités instantanément.' },
  ];

  return (
    <div className="home-pro">

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="hero-section" id="accueil">

        {/* Centered content */}
        <div className="hero-container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 36 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: 'easeOut' }}
          >


           

            <h1 className="hero-title">
 
  <span className="hero-tagline">Des points pour tout. Partout !</span>
</h1>

            <p className="hero-description">
             L'API de points qui débloque Stripe pour la région MENA<br/>
             
Pour les clients, pour les partenaires, sans frontières.

            </p>

            <div className="hero-buttons">
              <motion.button
                className="btn-hero-primary"
                onClick={handleRegisterClick}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                <RocketLaunchIcon style={{ width: 18, height: 18 }} />
                S'inscrire
              </motion.button>
              <motion.button
                className="btn-hero-secondary"
                onClick={handleLoginClick}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                <ArrowRightOnRectangleIcon style={{ width: 18, height: 18 }} />
                Se connecter
              </motion.button>
            </div>

            <div className="hero-stats">
              {[
                { number: '48+',   label: 'Partenaires' },
                { number: '2.5k+', label: 'Clients' },
                { number: '145k€', label: 'Transactions' },
              ].map((s, i) => (
                <motion.div
                  key={i}
                  className="stat-item"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                >
                  <span className="stat-number">{s.number}</span>
                  <span className="stat-label">{s.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ LE PROBLÈME ═══════════════ */}
      <section className="problem-section" id="probleme">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 32, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <span className="section-eyebrow">Le Problème</span>
            <h2>Vendre en ligne depuis la Tunisie<br />est encore trop compliqué</h2>
            <p className="section-description">
              Stripe, PayPal, Braintree — refusés ou limités pour les entreprises MENA.<br />
              POINTPAY est le pont entre vos services et les marchés internationaux.
            </p>
          </motion.div>

          <div className="problems-grid">
            {problems.map((p, i) => (
              <motion.div
                key={i}
                className="problem-card"
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.13, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="problem-icon">
                  {React.cloneElement(p.icon, { style: { width: 26, height: 26 } })}
                </div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ FEATURES (Atono style) ═══════════════ */}
      <section className="features-section" id="solution">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 32, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            
            <h2>Une solution complète pour<br />monétiser vos services</h2>
            <p className="section-description">
              Tout ce dont vous avez besoin pour accepter des paiements internationaux, gérer vos clients et suivre vos revenus.
            </p>
          </motion.div>

          <div className="features-grid">
            {features.map((f, i) => (
              <motion.div
                key={i}
                className="feature-card"
                initial={{ opacity: 0, x: i % 2 === 0 ? -32 : 32, y: 20 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ delay: i * 0.12, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6, boxShadow: '0 24px 48px rgba(37,99,235,0.13)' }}
              >
                <div className="feature-header">
                  <h3 className="feature-title">{f.title}</h3>
                  <div className="feature-icon-3d">
                    {React.cloneElement(f.icon, { style: { width: 28, height: 28 } })}
                  </div>
                </div>

                <div className="feature-body">
                  <p>{f.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ HOW IT WORKS ═══════════════ */}
      <section className="how-it-works-section" id="comment-ca-marche">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 32, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
           
            <h2>En 4 étapes simples</h2>
            <p className="section-description">De l'inscription à la vente de points, tout est simplifié</p>
          </motion.div>

          <div className="steps-grid">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                className="step-card"
                initial={{ opacity: 0, y: 44, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -5 }}
              >
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
                {i < steps.length - 1 && <div className="step-connector" />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ TESTIMONIALS ═══════════════ */}
      <section className="testimonials-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 32, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
           
            <h2>Ce que disent nos partenaires</h2>
          </motion.div>

          <div className="testimonials-grid">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                className="testimonial-card"
                initial={{ opacity: 0, y: 32, scale: 0.94 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.14, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -5 }}
              >
                <div className="testimonial-avatar">{t.avatar}</div>
                <div className="testimonial-rating">{'★'.repeat(t.rating)}</div>
                <p className="testimonial-text">"{t.content}"</p>
                <div className="testimonial-author">
                  <strong>{t.name}</strong>
                  <span>{t.role}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ PARTNER CTA ═══════════════ */}
      <section className="partner-cta-section">
        <div className="container">
          <motion.div
            className="partner-cta-card"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <div className="partner-cta-left">
              <div className="partner-cta-icon">
                <BuildingStorefrontIcon style={{ width: 32, height: 32 }} />
              </div>
              <div>
                <h2>Vous êtes une entreprise MENA ?</h2>
                <p>
                  Rejoignez POINTPAY en tant que partenaire. Intégrez notre API,
                  proposez des services à vos clients via des points Stripe, et percevez
                  votre commission mensuelle par virement bancaire.
                </p>
                <ul className="partner-cta-perks">
                  <li><span className="perk-check">✓</span> API REST documentée & clés d'accès dédiées</li>
                  <li><span className="perk-check">✓</span> Commission versée sous 30 jours après facture</li>
                  <li><span className="perk-check">✓</span> Dashboard partenaire en temps réel</li>
                  <li><span className="perk-check">✓</span> Validation sous 48h par l'équipe Runit-FR</li>
                </ul>
              </div>
            </div>
            <motion.button
              className="btn-partner-register"
              onClick={() => { navigate('/register/partner'); }}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <BuildingStorefrontIcon style={{ width: 18, height: 18 }} />
              Devenir partenaire
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 32, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <h2>Prêt à monétiser vos services ?</h2>
            <p>Rejoignez les 48 partenaires qui nous font déjà confiance</p>
            <div className="cta-buttons">
              <motion.button className="btn-hero-primary" onClick={handleRegisterClick} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <RocketLaunchIcon style={{ width: 18, height: 18 }} />
                Commencer maintenant
              </motion.button>
              <motion.button className="btn-cta-outline" onClick={handleLoginClick} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <ArrowRightOnRectangleIcon style={{ width: 18, height: 18 }} />
                Se connecter
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      <RoleSelectionModal
        isOpen={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        onSelect={handleRoleSelect}
        mode={modalMode}
      />
    </div>
  );
};

/* Mini visualizations inside each feature card */
const FeatureMiniViz = ({ type, color }) => {
  if (type === 'globe') return (
    <div className="mini-viz">
      {[40, 65, 50, 80, 55].map((h, i) => (
        <div key={i} className="mini-bar" style={{ height: h, background: color + (i === 3 ? 'ff' : '66') }} />
      ))}
    </div>
  );
  if (type === 'shield') return (
    <div className="mini-viz mini-shield">
      <div className="mini-pill" style={{ background: color + '22', color }}>✓ Vérifié</div>
      <div className="mini-pill" style={{ background: '#10b98122', color: '#10b981' }}>✓ Sécurisé</div>
    </div>
  );
  if (type === 'api') return (
    <div className="mini-viz mini-code">
      <div className="mini-line" style={{ width: '70%', background: color + '44' }} />
      <div className="mini-line" style={{ width: '50%', background: color + '66' }} />
      <div className="mini-line" style={{ width: '85%', background: color + '44' }} />
      <div className="mini-line" style={{ width: '40%', background: color + '88' }} />
    </div>
  );
  if (type === 'chart') return (
    <div className="mini-viz">
      {[30, 55, 45, 70, 60, 85, 75].map((h, i) => (
        <div key={i} className="mini-bar" style={{ height: h, background: i === 5 ? color : color + '55', borderRadius: 4 }} />
      ))}
    </div>
  );
  return null;
};

export default Home;
